import { useQuery } from "@tanstack/react-query";
import { api } from "../api/client";
import type { FeedbackSummary } from "../api/types";
import { FeedbackButtons } from "./FeedbackButtons";

interface Props {
  postId: string;
  summary: FeedbackSummary;
}

/**
 * Full feedback surface for the post-detail page.
 *  - Thumbs up/down with comment (uses shared <FeedbackButtons> in "full" variant)
 *  - Chronological history of every rating event (comments included)
 *
 * History is never mutated — new ratings append. The latest is the signal
 * used by the agents; the history shows how opinion has evolved.
 */
export function FeedbackPanel({ postId, summary }: Props) {
  const history = useQuery({
    queryKey: ["post-feedback", postId],
    queryFn: () => api.posts.feedbackHistory(postId),
  });

  const events = (history.data ?? []).slice().reverse(); // newest first

  return (
    <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-black/5">
      <div className="flex items-center justify-between">
        <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-brand-text-dark/60">
          Feedback
        </h3>
        <div className="flex items-center gap-3 text-xs text-brand-text-dark/60">
          {summary.up_count > 0 || summary.down_count > 0 ? (
            <span className="tabular-nums">
              <span className="text-emerald-700">↑ {summary.up_count}</span>
              <span className="mx-2 opacity-40">·</span>
              <span className="text-red-700">↓ {summary.down_count}</span>
            </span>
          ) : (
            <span className="italic opacity-50">noch keine Rückmeldung</span>
          )}
        </div>
      </div>

      <p className="mt-2 text-xs text-brand-text-dark/55">
        Die Agents nutzen die letzten Ratings (mit Kommentaren) als Beispiele bei
        der nächsten Generation. Kurze, konkrete Kommentare wirken am besten.
      </p>

      <div className="mt-4">
        <FeedbackButtons postId={postId} feedback={summary} variant="full" />
      </div>

      {events.length > 0 ? (
        <ol className="mt-6 space-y-3 border-t border-black/5 pt-4">
          {events.map((e, i) => (
            <li
              key={`${e.ts}-${i}`}
              className="flex items-start gap-3 text-sm"
            >
              <span
                className={`mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full ${
                  e.rating === "up"
                    ? "bg-emerald-500/15 text-emerald-700"
                    : "bg-red-500/15 text-red-700"
                }`}
                aria-label={e.rating}
              >
                {e.rating === "up" ? "↑" : "↓"}
              </span>
              <div className="flex-1">
                {e.comment ? (
                  <div className="text-brand-text-dark">{e.comment}</div>
                ) : (
                  <div className="italic text-brand-text-dark/50">kein Kommentar</div>
                )}
                <div className="mt-0.5 text-[11px] text-brand-text-dark/45">
                  {new Date(e.ts).toLocaleString("de-DE")}
                </div>
              </div>
            </li>
          ))}
        </ol>
      ) : null}
    </div>
  );
}
